import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Feature, Map as OlMap } from "ol";
import { Geometry, LineString } from "ol/geom";
import { Style, Stroke, Circle as CircleStyle, Fill } from "ol/style";
import { fromLonLat } from "ol/proj";
import { FeatureLike } from "ol/Feature";
import { EquipmentPosition } from "src/app/bo/hiboo/equipmentPosition";
import { EquipmentRoute, EquipmentRouteSegment } from "src/app/bo/equipment/equipmentRoute";
import { Coordinate } from "ol/coordinate";
import { MapBrowserEvent } from "ol";
import Point from "ol/geom/Point";
import { Subject } from "rxjs";
import { EquipmentRouteDto } from "src/app/bo/equipment/equipmentRoute";

/**
 * Helper function to calculate the squared distance from a point to another point.
 */
function distSquared(p1: Coordinate, p2: Coordinate) {
    const dx = p1[0] - p2[0];
    const dy = p1[1] - p2[1];
    return dx * dx + dy * dy;
}

/**
 * Helper function to calculate the squared distance from a point to a line segment.
 */
function distToSegmentSquared(p: Coordinate, v: Coordinate, w: Coordinate) {
  const l2 = distSquared(v, w);
  if (l2 === 0) return distSquared(p, v);
  let t = ((p[0] - v[0]) * (w[0] - v[0]) + (p[1] - v[1]) * (w[1] - v[1])) / l2;
  t = Math.max(0, Math.min(1, t));
  const closestPoint = [v[0] + t * (w[0] - v[0]), v[1] + t * (w[1] - v[1])];
  return distSquared(p, closestPoint);
}


/**
 * Layer pour afficher les routes (trajets) des équipements
 * Connecte les positions d'un même équipement chronologiquement pour une même journée
 */
export class EquipmentRouteLayer {
    private readonly source: VectorSource<Feature<Geometry>>;
    private readonly map: OlMap;
    private readonly layer: VectorLayer<VectorSource<Feature<Geometry>>>;
    private visible = false; // Par défaut masqué jusqu'à une recherche explicite
    private routes: EquipmentRoute[] = []; // Stocke toutes les routes créées
    private readonly routesByEquipmentCache: Map<number, EquipmentRoute[]> = new Map();
    private segments: EquipmentRouteSegment[] = []; // Stocke tous les segments créés
    private selectedRouteFeature: Feature<Geometry> | null = null; // Route actuellement sélectionnée
    private siteId = 0; // ID du site pour la sauvegarde des routes

    private currentFilterTime: Date | null = null;
    private splitMode = false; // Mode ciseaux activé/désactivé
    private mergeMode = false; // Mode fusion de routes activé/désactivé
    private firstRouteToMerge: EquipmentRoute | null = null; // Première route sélectionnée pour fusion
    private assignMode = false; // Mode assignation route à blast
    private routeToAssign: EquipmentRoute | null = null; // Route en attente d'assignation
    private clickListener: ((evt: MapBrowserEvent<any>) => void) | null = null;
    private hoverListener: ((evt: MapBrowserEvent<any>) => void) | null = null;
    private dblClickListener: (evt: MapBrowserEvent<any>) => void;
    private hoveredFeature: Feature<Geometry> | null = null; // Feature actuellement survolée

    public splitCompleted = new Subject<void>(); // Événement émis quand le split est terminé
    public mergeCompleted = new Subject<void>(); // Événement émis quand le merge est terminé
    public routesSaveRequired = new Subject<EquipmentRouteDto[]>(); // Événement émis quand il faut sauvegarder les routes
    public routesDeleteRequired = new Subject<number[]>(); // Événement émis quand il faut supprimer des routes en base
    public routeAssignmentRequested = new Subject<{ route: EquipmentRoute, tirId: number }>(); // Événement émis quand une route doit être assignée à un blast
    public routeSelected = new Subject<EquipmentRoute | null>(); // Événement émis quand une route est sélectionnée pour assignation

    // Style par défaut (opacité normale) - RÉDUIT
    private static readonly DEFAULT_STYLE = new Style({
        stroke: new Stroke({
            color: 'rgba(200, 200, 200, 0.8)', // Gris clair opacité 100%
            width: 2 // Réduit de 4 à 2
        })
    });

    // Couleurs très distinctes pour les routes (optimisées pour le contraste)
    private static readonly RANDOM_COLORS = [
        'rgba(230, 25, 75, 0.9)',    // Rouge vif
        'rgba(60, 180, 75, 0.9)',    // Vert vif
        'rgba(0, 130, 200, 0.9)',    // Bleu vif
        'rgba(255, 225, 25, 0.9)',   // Jaune vif
        'rgba(145, 30, 180, 0.9)',   // Violet
        'rgba(245, 130, 48, 0.9)',   // Orange
        'rgba(70, 240, 240, 0.9)',   // Cyan
        'rgba(240, 50, 230, 0.9)',   // Magenta
        'rgba(188, 246, 12, 0.9)',   // Lime
        'rgba(250, 190, 190, 0.9)',  // Rose
        'rgba(0, 128, 128, 0.9)',    // Teal
        'rgba(230, 190, 255, 0.9)',  // Lavande
        'rgba(154, 99, 36, 0.9)',    // Marron
        'rgba(255, 250, 200, 0.9)',  // Beige
        'rgba(128, 0, 0, 0.9)',      // Bordeaux
        'rgba(170, 255, 195, 0.9)',  // Menthe
        'rgba(128, 128, 0, 0.9)',    // Olive
        'rgba(255, 215, 180, 0.9)',  // Pêche
        'rgba(0, 0, 128, 0.9)'       // Navy
    ];

    /**
     * Génère une couleur unique pour un équipement donné
     * Évite les couleurs déjà utilisées par les autres routes du même équipement
     */
    private getUniqueColorForEquipment(equipmentId: number, excludeColors: string[] = []): string {
        // Récupérer les couleurs déjà utilisées par cet équipement
        const usedColors = this.routes
            .filter(r => r.equipmentId === equipmentId)
            .map(r => r.color);

        // Combiner avec les couleurs à exclure
        const allExcludedColors = [...usedColors, ...excludeColors];

        // Trouver une couleur disponible
        const availableColors = EquipmentRouteLayer.RANDOM_COLORS.filter(
            color => !allExcludedColors.includes(color)
        );

        if (availableColors.length > 0) {
            // Choisir une couleur aléatoire parmi les disponibles
            const randomIndex = Math.floor(Math.random() * availableColors.length);
            return availableColors[randomIndex];
        }

        // Si toutes les couleurs sont utilisées, en choisir une aléatoire
        const randomIndex = Math.floor(Math.random() * EquipmentRouteLayer.RANDOM_COLORS.length);
        return EquipmentRouteLayer.RANDOM_COLORS[randomIndex];
    }

    /**
     * Déduplique les positions par timestamp
     */
    private deduplicatePositions(positions: EquipmentPosition[]): EquipmentPosition[] {
        const seen = new Set<number>();
        return positions.filter(pos => {
            const timestamp = new Date(pos.date).getTime();
            if (seen.has(timestamp)) return false;
            seen.add(timestamp);
            return true;
        });
    }

    constructor(map: OlMap, zIndex: number) {
        this.map = map;
        this.source = new VectorSource();
        this.layer = new VectorLayer({
            source: this.source,
            style: (feature) => this.routeStyleFunction(feature),
        });

        this.layer.setZIndex(zIndex);
        this.layer.setVisible(false); // Masquer les routes par défaut
        this.map.addLayer(this.layer);

        // Double-clic sur une route = split direct (raccourci)
        this.dblClickListener = (evt: MapBrowserEvent<any>) => {
            if (!this.visible || this.mergeMode || this.assignMode) return;

            const result = this.findClosestRouteAndPosition(evt.coordinate);
            if (result) {
                evt.stopPropagation();
                evt.preventDefault();
                this.splitRouteAtPosition(result.route, result.positionIndex);
                this.splitCompleted.next();
            }
        };
        this.map.on('dblclick', this.dblClickListener);
    }

    /**
     * Efface toutes les routes
     */
    public clear() {
        this.source.clear();
        this.routes = [];
        this.routesByEquipmentCache.clear();
        this.segments = [];
        this.selectedRouteFeature = null;
    }

    /**
     * Crée les routes à partir des positions d'équipements
     * Groupe les positions par équipement et par jour, puis les connecte chronologiquement
     */
    public createRoutesFromPositions(positions: EquipmentPosition[], equipmentMap?: Map<number, any>, savedRoutes?: EquipmentRouteDto[]) {
        this.clear();

        // Grouper les routes sauvegardées par equipmentId
        const savedRoutesByEquipment = new Map<number, EquipmentRouteDto[]>();
        if (savedRoutes && savedRoutes.length > 0) {
            savedRoutes.forEach(route => {
                if (!savedRoutesByEquipment.has(route.equipmentId)) {
                    savedRoutesByEquipment.set(route.equipmentId, []);
                }
                savedRoutesByEquipment.get(route.equipmentId)!.push(route);
            });
        }

        // Grouper les positions par équipement et par jour
        const groupedPositions = this.groupPositionsByEquipmentAndDay(positions);

        // Pour chaque groupe, créer une ligne reliant les positions
        groupedPositions.forEach((dayGroups, equipmentId) => {
            // Si cet équipement a des routes sauvegardées, les utiliser au lieu de créer automatiquement
            if (savedRoutesByEquipment.has(equipmentId)) {
                const savedEquipmentRoutes = savedRoutesByEquipment.get(equipmentId)!;
                const allPositionsForEquipment = Array.from(dayGroups.values()).flat();
                const equipmentName = equipmentMap?.get(equipmentId)?.displayName || `Equipment ${equipmentId}`;

                let routesCreated = 0;
                savedEquipmentRoutes.forEach((savedRoute) => {
                    // Filtrer les positions qui sont dans la plage de temps de cette route sauvegardée
                    const routePositions = allPositionsForEquipment.filter(p => {
                        const posDate = new Date(p.date).getTime();
                        const startDate = new Date(savedRoute.start).getTime();
                        const endDate = new Date(savedRoute.end).getTime();
                        return posDate >= startDate && posDate <= endDate;
                    });

                    if (routePositions.length >= 1) {
                        // Générer une couleur unique pour cet équipement
                        const color = this.getUniqueColorForEquipment(equipmentId);
                        // Passer l'ID et le tirId de la route sauvegardée
                        this.createAndAddRoute(routePositions, equipmentId, equipmentName, color, {
                            id: savedRoute.id,
                            tirId: savedRoute.tirId
                        });
                        routesCreated++;
                    }
                });

                // Si aucune route n'a été créée à partir des routes sauvegardées, créer automatiquement
                if (routesCreated === 0) {
                    // Ne pas faire return, continuer pour créer les routes automatiquement
                } else {
                    return; // Passer à l'équipement suivant seulement si des routes ont été créées
                }
            }

            // Pas de routes sauvegardées, créer les routes automatiquement
            dayGroups.forEach((positions, dayKey) => {
                if (positions.length === 0) {
                    return;
                }

                // Trier les positions par date/heure
                const sortedPositions = positions.sort((a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                );

                // Créer les coordonnées pour la ligne
                // Si une seule position, dupliquer le point pour créer un LineString valide
                const coordinates = sortedPositions.length === 1
                    ? [fromLonLat([sortedPositions[0].longitude, sortedPositions[0].latitude]),
                       fromLonLat([sortedPositions[0].longitude, sortedPositions[0].latitude])]
                    : sortedPositions.map(pos => fromLonLat([pos.longitude, pos.latitude]));

                // Obtenir les heures de début et fin
                const startTime = new Date(sortedPositions[0].date);
                const endTime = new Date(sortedPositions[sortedPositions.length - 1].date);

                // Obtenir une couleur basée sur l'ID de l'équipement pour garantir la cohérence
                const color = EquipmentRouteLayer.RANDOM_COLORS[equipmentId % EquipmentRouteLayer.RANDOM_COLORS.length];

                // Créer la feature LineString principale (référence logique de la route)
                const lineFeature = new Feature({
                    geometry: new LineString(coordinates),
                    equipmentId: equipmentId,
                    positions: sortedPositions,
                    dayKey: dayKey,
                    startTime: startTime,
                    endTime: endTime,
                    color: color,
                    isSelected: true, // Par défaut, la route est visible
                    equipmentSelected: true // Par défaut, l'équipement est sélectionné
                });

                // Stocker les informations de la route
                const equipmentName = equipmentMap?.get(equipmentId)?.displayName || `Equipment ${equipmentId}`;
                const route: EquipmentRoute = {
                    equipmentId: equipmentId,
                    equipmentName: equipmentName,
                    dayKey: dayKey,
                    startTime: startTime,
                    endTime: endTime,
                    color: color,
                    feature: lineFeature,
                    isSelected: true // Par défaut, la route est visible
                };

                this.routes.push(route);
                this.routesByEquipmentCache.clear();

                // Créer des segments visuels séparés entre chaque paire de positions
                // pour permettre l'opacité individuelle selon le temps
                for (let i = 0; i < sortedPositions.length - 1; i++) {
                    const pos1 = sortedPositions[i];
                    const pos2 = sortedPositions[i + 1];

                    // Coordonnées du segment visuel
                    const segmentCoords = [
                        fromLonLat([pos1.longitude, pos1.latitude]),
                        fromLonLat([pos2.longitude, pos2.latitude])
                    ];

                    // Dates du segment
                    const segmentStartTime = new Date(pos1.date);
                    const segmentEndTime = new Date(pos2.date);

                    // Créer une feature visuelle pour ce segment
                    const segmentFeature = new Feature({
                        geometry: new LineString(segmentCoords),
                        equipmentId: equipmentId,
                        dayKey: dayKey,
                        startTime: segmentStartTime,
                        endTime: segmentEndTime,
                        color: color,
                        isSelected: true, // Par défaut, le segment est visible
                        equipmentSelected: true, // Par défaut, l'équipement est sélectionné
                        isVisualSegment: true, // Marqueur pour identifier les segments visuels
                        parentRouteId: equipmentId + '_' + dayKey + '_' + startTime.getTime() // ID unique basé sur le timestamp
                    });

                    this.source.addFeature(segmentFeature);
                }
            });
        });
    }

    /**
     * Crée des segments de route basés sur des plages de temps définies
     * Permet de diviser une route d'équipement en plusieurs segments (par ex. par tir/blast)
     */
    public createRouteSegmentsFromPositions(
        positions: EquipmentPosition[],
        segments: EquipmentRouteSegment[],
        equipmentMap?: Map<number, any>
    ) {
        this.clear();

        segments.forEach((segment, index) => {
            // Filter positions within segment time range
            const segmentPositions = positions.filter(pos => {
                const posDate = new Date(pos.date);
                return pos.equipmentExternalId === segment.equipmentId &&
                       posDate >= segment.dateBegin &&
                       posDate <= segment.dateEnd;
            });

            if (segmentPositions.length < 2) {
                return; // Pas assez de points pour tracer une ligne
            }

            // Sort positions by date
            const sortedPositions = segmentPositions.sort((a, b) =>
                new Date(a.date).getTime() - new Date(b.date).getTime()
            );

            // Create coordinates for the line
            const coordinates = sortedPositions.map(pos =>
                fromLonLat([pos.longitude, pos.latitude])
            );

            // Assign color based on tirId or use default
            const color = segment.color || this.getColorForSegment(index, segment.tirId);

            // Create feature with segment metadata
            const lineFeature = new Feature({
                geometry: new LineString(coordinates),
                equipmentId: segment.equipmentId,
                tirId: segment.tirId,
                segmentIndex: index,
                dateBegin: segment.dateBegin,
                dateEnd: segment.dateEnd,
                positions: sortedPositions,
                isSelected: true, // Par défaut, le segment est visible
                equipmentSelected: true, // Par défaut, l'équipement est sélectionné
                isSegment: true // Marqueur pour différencier d'une route normale
            });

            this.source.addFeature(lineFeature);

            // Update segment with feature reference
            segment.feature = lineFeature;
            segment.color = color;

            this.segments.push(segment);
        });
    }

    /**
     * Génère une couleur pour un segment
     * Utilise différentes couleurs selon le tirId ou l'index
     */
    private getColorForSegment(index: number, tirId?: number): string {
        // Si un tirId est fourni, utiliser une couleur basée sur le tirId
        if (tirId !== undefined && tirId !== null) {
            const colors = [
                'rgba(255, 193, 7, 0.9)',   // Jaune/Orange
                'rgba(33, 150, 243, 0.9)',  // Bleu
                'rgba(156, 39, 176, 0.9)',  // Violet
                'rgba(244, 67, 54, 0.9)',   // Rouge
                'rgba(76, 175, 80, 0.9)',   // Vert
                'rgba(255, 87, 34, 0.9)',   // Orange profond
                'rgba(0, 188, 212, 0.9)',   // Cyan
            ];
            return colors[tirId % colors.length];
        }

        // Sinon, utiliser une couleur basée sur l'index
        const colors = [
            'rgba(255, 193, 7, 0.9)',   // Jaune/Orange
            'rgba(33, 150, 243, 0.9)',  // Bleu
            'rgba(156, 39, 176, 0.9)',  // Violet
            'rgba(244, 67, 54, 0.9)',   // Rouge
            'rgba(76, 175, 80, 0.9)',   // Vert
        ];
        return colors[index % colors.length];
    }

    /**
     * Crée un style spécifique pour un segment avec la couleur donnée
     */
    private createSegmentStyle(color: string, isFaded = false): Style {
        // Ajuster l'opacité si le segment est dans le futur
        const adjustedColor = isFaded ? this.fadeColor(color) : color;

        return new Style({
            stroke: new Stroke({
                color: adjustedColor,
                width: 2 // Réduit de 4 à 2
            })
        });
    }

    /**
     * Réduit l'opacité d'une couleur rgba de 50%
     */
    private fadeColor(color: string): string {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
        if (match) {
            const r = match[1];
            const g = match[2];
            const b = match[3];
            const a = match[4] ? parseFloat(match[4]) : 1;
            return `rgba(${r}, ${g}, ${b}, ${a * 0.5})`;
        }
        return color;
    }

    /**
     * Groupe les positions par équipement et par période de shift
     * Gère le cas du shift de nuit (22h-6h) qui traverse minuit
     * @returns Map<equipmentId, Map<shiftKey, positions[]>>
     */
    private groupPositionsByEquipmentAndDay(
        positions: EquipmentPosition[]
    ): Map<number, Map<string, EquipmentPosition[]>> {
        const grouped = new Map<number, Map<string, EquipmentPosition[]>>();

        positions.forEach((position) => {
            const equipmentId = position.equipmentExternalId;
            const date = new Date(position.date);
            const shiftKey = this.getShiftKey(date);

            if (!grouped.has(equipmentId)) {
                grouped.set(equipmentId, new Map<string, EquipmentPosition[]>());
            }

            const equipmentGroups = grouped.get(equipmentId)!;
            if (!equipmentGroups.has(shiftKey)) {
                equipmentGroups.set(shiftKey, []);
            }

            equipmentGroups.get(shiftKey)!.push(position);
        });

        return grouped;
    }

    /**
     * Génère une clé unique pour une période de shift
     * Pour le shift de nuit (22h-6h), les positions de 0h-5h59 sont rattachées au jour précédent
     * Format: YYYY-MM-DD_SHIFT (où SHIFT = morning, afternoon, night)
     */
    private getShiftKey(date: Date): string {
        const hour = date.getHours();
        let shiftDate = new Date(date);
        let shiftName: string;

        if (hour >= 6 && hour < 14) {
            // Shift matin (6h-14h)
            shiftName = 'morning';
        } else if (hour >= 14 && hour < 22) {
            // Shift après-midi (14h-22h)
            shiftName = 'afternoon';
        } else {
            // Shift nuit (22h-6h)
            shiftName = 'night';
            // Si l'heure est entre 0h et 6h, rattacher au jour précédent
            if (hour < 6) {
                shiftDate = new Date(date.getTime() - 24 * 60 * 60 * 1000);
            }
        }

        const dayKey = `${shiftDate.getFullYear()}-${String(shiftDate.getMonth() + 1).padStart(2, '0')}-${String(shiftDate.getDate()).padStart(2, '0')}`;
        return `${dayKey}_${shiftName}`;
    }

    /**
     * Génère une clé unique pour un jour (YYYY-MM-DD)
     */
    private getDayKey(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    // Rayon de temps en millisecondes (10 minutes)
    private static readonly TIME_RADIUS_MS = 10 * 60 * 1000;

    /**
     * Fonction de style pour les routes
     */
    private routeStyleFunction(feature: FeatureLike): Style | null {
        const isSelected = feature.get("isSelected");
        const equipmentSelected = feature.get("equipmentSelected");

        // Si la route n'est pas sélectionnée (checkbox route) OU si l'équipement n'est pas sélectionné (checkbox équipement), masquer la route
        // Par défaut, si equipmentSelected n'est pas défini, on considère que l'équipement est sélectionné
        if (isSelected === false || (equipmentSelected !== undefined && equipmentSelected === false)) {
            return null; // Masquer la route
        }

        // Vérifier si c'est un segment visuel
        const isVisualSegment = feature.get("isVisualSegment");
        if (isVisualSegment) {
            // C'est un segment visuel, récupérer la couleur depuis la route parente
            const parentRouteId = feature.get("parentRouteId") as string;
            const equipmentId = feature.get("equipmentId") as number;

            // Trouver la route parente pour obtenir sa couleur actuelle
            let featureColor = feature.get("color") as string;
            const parentRoute = this.routes.find(r =>
                r.equipmentId === equipmentId &&
                (r.equipmentId + '_' + r.dayKey + '_' + r.startTime.getTime()) === parentRouteId
            );
            if (parentRoute) {
                featureColor = parentRoute.color;
            }

            // Fallback: si pas de couleur, utiliser la même logique que BlastEquipmentLayer
            // pour garantir la cohérence des couleurs entre positions et chemins
            if (!featureColor && equipmentId !== undefined) {
                featureColor = EquipmentRouteLayer.RANDOM_COLORS[equipmentId % EquipmentRouteLayer.RANDOM_COLORS.length];
            }

            const startTime = feature.get("startTime");
            const endTime = feature.get("endTime");

            if (featureColor) {
                // Si pas de filtre de temps OU pas de startTime, afficher normalement avec la couleur
                if (!this.currentFilterTime || !startTime) {
                    return new Style({
                        stroke: new Stroke({
                            color: featureColor,
                            width: 2
                        })
                    });
                }

                const filterTimeMs = this.currentFilterTime.getTime();
                const segmentStartMs = new Date(startTime).getTime();
                const segmentEndMs = endTime ? new Date(endTime).getTime() : segmentStartMs;

                // Vérifier si le segment est dans le rayon de 10 minutes
                const isWithinRadius = this.isTimeWithinRadius(segmentStartMs, segmentEndMs, filterTimeMs);

                if (!isWithinRadius) {
                    // Hors du rayon de 10 minutes -> opacité très réduite (10%)
                    const fadedColor = this.setColorOpacity(featureColor, 0.1);
                    return new Style({
                        stroke: new Stroke({
                            color: fadedColor,
                            width: 1
                        })
                    });
                }

                // Dans le rayon mais dans le futur -> fade à 50%
                const isFuture = segmentStartMs > filterTimeMs;
                const color = isFuture ? this.fadeColor(featureColor) : featureColor;
                return new Style({
                    stroke: new Stroke({
                        color: color,
                        width: 2
                    })
                });
            }
        }

        // C'est la feature principale (logique) - ne pas la rendre visible
        const positions = feature.get("positions");
        if (positions) {
            // Cette feature est juste une référence logique, on ne la dessine pas
            return null;
        }

        // Vérifier si c'est un segment créé par split (avec couleur personnalisée)
        const isSegment = feature.get("isSegment");
        if (isSegment) {
            const segmentIndex = feature.get("segmentIndex") as number;
            const segment = this.segments[segmentIndex];

            if (segment && segment.color) {
                // Si pas de filtre de temps, afficher normalement
                if (!this.currentFilterTime) {
                    return this.createSegmentStyle(segment.color, false);
                }

                const filterTimeMs = this.currentFilterTime.getTime();
                const segmentStartMs = new Date(feature.get("dateBegin")).getTime();
                const segmentEndMs = feature.get("dateEnd") ? new Date(feature.get("dateEnd")).getTime() : segmentStartMs;

                // Vérifier si le segment est dans le rayon de 10 minutes
                const isWithinRadius = this.isTimeWithinRadius(segmentStartMs, segmentEndMs, filterTimeMs);

                if (!isWithinRadius) {
                    // Hors du rayon -> opacité très réduite
                    const fadedColor = this.setColorOpacity(segment.color, 0.1);
                    return new Style({
                        stroke: new Stroke({
                            color: fadedColor,
                            width: 1
                        })
                    });
                }

                const isFuture = segmentStartMs > filterTimeMs;
                return this.createSegmentStyle(segment.color, isFuture);
            }
        }

        // Fallback
        return EquipmentRouteLayer.DEFAULT_STYLE;
    }

    /**
     * Vérifie si les deux extrémités d'un segment sont dans le rayon de 10 minutes du temps de filtre
     * Le segment est visible à 100% seulement si les deux positions (début et fin) sont dans le rayon
     */
    private isTimeWithinRadius(segmentStartMs: number, segmentEndMs: number, filterTimeMs: number): boolean {
        const radiusStart = filterTimeMs - EquipmentRouteLayer.TIME_RADIUS_MS;
        const radiusEnd = filterTimeMs + EquipmentRouteLayer.TIME_RADIUS_MS;

        // Les deux extrémités du segment doivent être dans le rayon
        const startInRadius = segmentStartMs >= radiusStart && segmentStartMs <= radiusEnd;
        const endInRadius = segmentEndMs >= radiusStart && segmentEndMs <= radiusEnd;

        return startInRadius && endInRadius;
    }

    /**
     * Définit l'opacité d'une couleur rgba
     */
    private setColorOpacity(color: string, opacity: number): string {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
        if (match) {
            const r = match[1];
            const g = match[2];
            const b = match[3];
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        return color;
    }

    /**
     * Affiche ou masque la couche des routes
     */
    public setVisible(visible: boolean) {
        this.visible = visible;
        this.layer.setVisible(visible);
    }

    /**
     * Alias pour setVisible - pour compatibilité avec le code existant
     */
    public setRoutesVisible(visible: boolean) {
        this.setVisible(visible);
    }

    /**
     * Retourne la visibilité actuelle de la couche
     */
    public isVisible(): boolean {
        return this.visible;
    }

    /**
     * Obtient la couche OpenLayers
     */
    public getLayer(): VectorLayer<VectorSource<Feature<Geometry>>> {
        return this.layer;
    }

    /**
     * Recentre la carte sur l'étendue d'une route
     */
    public zoomToRoute(route: EquipmentRoute): void {
        const geometry = route.feature.getGeometry();
        if (!geometry) return;
        const extent = geometry.getExtent();
        this.map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 18, duration: 300 });
    }

    /**
     * Retourne toutes les routes créées
     */
    public getRoutes(): EquipmentRoute[] {
        return this.routes;
    }

    /**
     * Retourne les routes pour un équipement spécifique
     */
    public getRoutesForEquipment(equipmentId: number): EquipmentRoute[] {
        const cached = this.routesByEquipmentCache.get(equipmentId);
        if (cached) {
            return cached;
        }
        const sorted = this.routes
            .filter(r => r.equipmentId === equipmentId)
            .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        this.routesByEquipmentCache.set(equipmentId, sorted);
        return sorted;
    }

    /**
     * Retourne tous les segments créés
     */
    public getSegments(): EquipmentRouteSegment[] {
        return this.segments;
    }

    /**
     * Retourne les segments pour un équipement spécifique
     */
    public getSegmentsForEquipment(equipmentId: number): EquipmentRouteSegment[] {
        return this.segments.filter(s => s.equipmentId === equipmentId);
    }

    /**
     * Met à jour le temps de filtrage et rafraîchit les styles des routes
     */
    public setFilterTime(filterTime: Date | null): void {
        this.currentFilterTime = filterTime;
        // Forcer le rafraîchissement des styles de toutes les routes
        this.layer.changed();
    }

    /**
     * Filtre les routes visibles par les IDs d'équipements sélectionnés
     */
    public filterRoutesByEquipmentIds(selectedEquipmentIds: number[]): void {
        const features = this.source.getFeatures();
        features.forEach(feature => {
            const equipmentId = feature.get('equipmentId');
            if (equipmentId !== undefined) {
                const isEquipmentSelected = selectedEquipmentIds.includes(equipmentId);
                feature.set('equipmentSelected', isEquipmentSelected);
            }
        });

        // Forcer le refresh complet de la couche
        this.source.changed();
        this.layer.changed();
    }

    /**
     * Toggle la visibilité d'une route
     */
    public selectRoute(route: EquipmentRoute) {
        // Toggle l'état de visibilité
        const newState = !route.isSelected;
        route.isSelected = newState;
        route.feature.set("isSelected", newState);
        route.feature.changed();

        // Mettre à jour tous les segments visuels de cette route
        const parentRouteId = route.equipmentId + '_' + route.dayKey + '_' + route.startTime.getTime();
        const allFeatures = this.source.getFeatures();
        allFeatures.forEach(feature => {
            if (feature.get('parentRouteId') === parentRouteId) {
                feature.set('isSelected', newState);
                feature.changed();
            }
        });

        // Forcer le re-rendu
        this.layer.changed();
    }

    /**
     * Désélectionne toutes les routes
     */
    public deselectAllRoutes() {
        if (this.selectedRouteFeature) {
            this.selectedRouteFeature.set("isSelected", false);
            this.selectedRouteFeature.changed();
            this.selectedRouteFeature = null;
        }

        this.routes.forEach(route => {
            route.isSelected = false;
            route.feature.set("isSelected", false);
        });

        this.layer.changed();
    }

    /**
     * Supprime une route du layer
     * @param deleteFromDb Si true, émet l'événement pour supprimer la route en base
     */
    public removeRoute(route: EquipmentRoute, deleteFromDb = false): void {
        // Supprimer la feature principale
        this.source.removeFeature(route.feature);

        // Supprimer les segments visuels associés
        const parentRouteId = route.equipmentId + '_' + route.dayKey + '_' + route.startTime.getTime();
        const featuresToRemove: Feature<Geometry>[] = [];
        this.source.getFeatures().forEach(feature => {
            if (feature.get('isVisualSegment') && feature.get('parentRouteId') === parentRouteId) {
                featuresToRemove.push(feature);
            }
        });
        featuresToRemove.forEach(feature => this.source.removeFeature(feature));

        // Émettre l'événement pour supprimer la route en base si elle a un ID
        if (deleteFromDb && route.id) {
            this.routesDeleteRequired.next([route.id]);
        }

        // Supprimer de la liste interne
        this.routes = this.routes.filter(r => r !== route);
        this.routesByEquipmentCache.clear();

        this.layer.changed();
    }

    /**
     * Crée une route fusionnée à partir de positions
     */
    public createMergedRoute(
        positions: EquipmentPosition[],
        equipmentId: number,
        equipmentName: string,
        color: string
    ): EquipmentRoute | null {
        // Dédupliquer et trier les positions
        const uniquePositions = this.deduplicatePositions(positions);
        const sortedPositions = uniquePositions.sort((a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        const mergedRoute = this.createAndAddRoute(sortedPositions, equipmentId, equipmentName, color);

        if (mergedRoute) {
            // Émettre l'événement pour sauvegarder la route fusionnée
            const routeDtos = this.convertRoutesToDtos([mergedRoute], this.siteId);
            this.routesSaveRequired.next(routeDtos);

            // Notifier que le merge est terminé
            this.mergeCompleted.next();
        }

        this.layer.changed();
        return mergedRoute;
    }

    /**
     * Active ou désactive le mode split (ciseaux)
     */
    public setSplitMode(enabled: boolean): void {
        this.splitMode = enabled;

        // Changer le curseur de la carte
        const viewport = this.map.getViewport();
        if (enabled) {
            viewport.style.cursor = 'crosshair'; // ou une classe CSS personnalisée pour des ciseaux
            this.addClickListener();
            this.addHoverListener();
        } else {
            viewport.style.cursor = '';
            this.removeClickListener();
            this.removeHoverListener();
        }
    }

    /**
     * Retourne si le mode split est activé
     */
    public isSplitModeEnabled(): boolean {
        return this.splitMode;
    }

    /**
     * Active ou désactive le mode merge (fusion de routes)
     */
    public setMergeMode(enabled: boolean): void {
        this.mergeMode = enabled;
        // Nettoyer la sélection précédente avant de changer de mode
        this.clearMergeSelection();

        // Changer le curseur de la carte
        const viewport = this.map.getViewport();
        if (enabled) {
            viewport.style.cursor = 'pointer';
            this.addClickListener();
            this.addHoverListener();
        } else {
            viewport.style.cursor = '';
            // Ne supprimer les listeners que si le mode split n'est pas actif
            if (!this.splitMode) {
                this.removeClickListener();
                this.removeHoverListener();
            }
        }
    }

    /**
     * Retourne si le mode merge est activé
     */
    public isMergeModeEnabled(): boolean {
        return this.mergeMode;
    }

    /**
     * Efface la sélection de merge en cours
     */
    private clearMergeSelection(): void {
        if (this.firstRouteToMerge) {
            // Réinitialiser le style de la route
            this.firstRouteToMerge.feature.setStyle(undefined);
            this.firstRouteToMerge = null;
        }
    }

    /**
     * Fusionne deux routes en une seule
     */
    private mergeRoutes(route1: EquipmentRoute, route2: EquipmentRoute): void {
        // Vérifier que les deux routes appartiennent au même équipement
        if (route1.equipmentId !== route2.equipmentId) {
            return;
        }

        // Récupérer les positions des deux routes
        const positions1 = route1.feature.get('positions') as EquipmentPosition[];
        const positions2 = route2.feature.get('positions') as EquipmentPosition[];

        if (!positions1 || !positions2) {
            return;
        }

        // Combiner les positions, dédupliquer et trier chronologiquement
        const combinedPositions = [...positions1, ...positions2];
        const uniquePositions = this.deduplicatePositions(combinedPositions);
        const mergedPositions = uniquePositions.sort((a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        // Supprimer les anciennes routes
        this.source.removeFeature(route1.feature);
        this.source.removeFeature(route2.feature);

        // Supprimer les segments visuels associés
        const parentRouteId1 = route1.equipmentId + '_' + route1.dayKey + '_' + route1.startTime.getTime();
        const parentRouteId2 = route2.equipmentId + '_' + route2.dayKey + '_' + route2.startTime.getTime();
        const featuresToRemove: Feature<Geometry>[] = [];
        this.source.getFeatures().forEach(feature => {
            const parentId = feature.get('parentRouteId');
            if (feature.get('isVisualSegment') && (parentId === parentRouteId1 || parentId === parentRouteId2)) {
                featuresToRemove.push(feature);
            }
        });
        featuresToRemove.forEach(feature => this.source.removeFeature(feature));

        // Collecter les IDs des routes à supprimer en base (si elles existent)
        const routeIdsToDelete: number[] = [];
        if (route1.id) routeIdsToDelete.push(route1.id);
        if (route2.id) routeIdsToDelete.push(route2.id);

        // Supprimer les routes de la liste interne
        this.routes = this.routes.filter(r => r.feature !== route1.feature && r.feature !== route2.feature);
        this.routesByEquipmentCache.clear();

        // Émettre l'événement pour supprimer les anciennes routes en base
        if (routeIdsToDelete.length > 0) {
            this.routesDeleteRequired.next(routeIdsToDelete);
        }

        // Créer la nouvelle route fusionnée (garder la couleur de la première route)
        const mergedRoute = this.createAndAddRoute(
            mergedPositions,
            route1.equipmentId,
            route1.equipmentName,
            route1.color
        );

        if (mergedRoute) {
            // Émettre l'événement pour sauvegarder la route fusionnée
            const routeDtos = this.convertRoutesToDtos([mergedRoute], this.siteId);
            this.routesSaveRequired.next(routeDtos);
        }

        // Forcer le rafraîchissement de la couche
        this.layer.changed();

        // Notifier que le merge est terminé
        this.mergeCompleted.next();
    }

    /**
     * Active ou désactive le mode assignation de route à un blast
     */
    public setAssignMode(enabled: boolean, route?: EquipmentRoute): void {
        this.assignMode = enabled;

        if (enabled && route) {
            this.routeToAssign = route;
            this.routeSelected.next(route);
            // Changer le curseur pour indiquer le mode assignation
            const viewport = this.map.getViewport();
            viewport.style.cursor = 'crosshair';
        } else {
            this.routeToAssign = null;
            this.routeSelected.next(null);
            // Restaurer le curseur normal
            const viewport = this.map.getViewport();
            viewport.style.cursor = '';
        }
    }

    /**
     * Retourne si le mode assignation est activé
     */
    public isAssignModeEnabled(): boolean {
        return this.assignMode;
    }

    /**
     * Retourne la route en attente d'assignation
     */
    public getRouteToAssign(): EquipmentRoute | null {
        return this.routeToAssign;
    }

    /**
     * Assigne une route à un blast (tirId)
     */
    public assignRouteToBlast(tirId: number): void {
        if (!this.routeToAssign) {
            return;
        }

        const route = this.routeToAssign;

        // Émettre l'événement pour que le composant parent sauvegarde l'assignation
        this.routeAssignmentRequested.next({ route, tirId });

        // Désactiver le mode assignation
        this.setAssignMode(false);
    }

    /**
     * Vérifie si un segment visuel est dans le rayon de temps de 10 minutes
     */
    private isSegmentWithinTimeRadius(feature: Feature<Geometry>): boolean {
        if (!this.currentFilterTime) {
            return true; // Pas de filtre de temps, tout est accessible
        }

        const startTime = feature.get('startTime');
        const endTime = feature.get('endTime');

        if (!startTime) {
            return false;
        }

        const filterTimeMs = this.currentFilterTime.getTime();
        const segmentStartMs = new Date(startTime).getTime();
        const segmentEndMs = endTime ? new Date(endTime).getTime() : segmentStartMs;

        return this.isTimeWithinRadius(segmentStartMs, segmentEndMs, filterTimeMs);
    }

    /**
     * Vérifie si au moins une extrémité d'un segment est dans le rayon de temps.
     * Utilisé pour le split : si une position est mise en avant par le slider,
     * les routes reliées à cette position doivent être splittables.
     */
    private isSegmentConnectedToTimeRadius(feature: Feature<Geometry>): boolean {
        if (!this.currentFilterTime) {
            return true;
        }

        const startTime = feature.get('startTime');
        const endTime = feature.get('endTime');

        if (!startTime) {
            return false;
        }

        const filterTimeMs = this.currentFilterTime.getTime();
        const segmentStartMs = new Date(startTime).getTime();
        const segmentEndMs = endTime ? new Date(endTime).getTime() : segmentStartMs;

        // Au moins une extrémité dans le rayon (vs isTimeWithinRadius qui exige les deux)
        return this.isTimeWithinRadius(segmentStartMs, segmentStartMs, filterTimeMs)
            || this.isTimeWithinRadius(segmentEndMs, segmentEndMs, filterTimeMs);
    }

    /**
     * Trouve une route à partir d'un clic sur la carte
     * Ne retourne que les routes dont au moins un segment est dans le rayon de 10 minutes
     */
    public findRouteAtPixel(pixel: number[]): EquipmentRoute | null {
        // En mode split, un segment est cliquable si au moins une extrémité
        // est dans le rayon de temps (la position mise en avant par le slider)
        const checkSegment = this.splitMode
            ? (f: Feature<Geometry>) => this.isSegmentConnectedToTimeRadius(f)
            : (f: Feature<Geometry>) => this.isSegmentWithinTimeRadius(f);

        // D'abord essayer la détection standard
        let feature = this.map.forEachFeatureAtPixel(pixel, (feature) => {
            if (feature.get('isVisualSegment') && checkSegment(feature as Feature<Geometry>)) {
                return feature as Feature<Geometry>;
            }
            return null;
        }, {
            layerFilter: (layer) => layer === this.layer,
            hitTolerance: 10 // Augmenter la tolérance de hit pour les lignes fines
        });

        // Si pas trouvé avec la détection standard, chercher manuellement avec une tolérance plus grande
        if (!feature) {
            const clickCoordinate = this.map.getCoordinateFromPixel(pixel);
            if (clickCoordinate) {
                let minDistance = Infinity;
                const tolerance = 20; // Tolérance en pixels

                this.source.getFeatures().forEach(f => {
                    if (!f.get('isVisualSegment')) return;

                    // Ignorer les segments hors du rayon de temps
                    if (!checkSegment(f)) return;

                    const geometry = f.getGeometry() as LineString;
                    if (!geometry) return;

                    const closestPoint = geometry.getClosestPoint(clickCoordinate);
                    const closestPixel = this.map.getPixelFromCoordinate(closestPoint);
                    const pixelDistance = Math.sqrt(
                        Math.pow(pixel[0] - closestPixel[0], 2) +
                        Math.pow(pixel[1] - closestPixel[1], 2)
                    );

                    if (pixelDistance < tolerance && pixelDistance < minDistance) {
                        minDistance = pixelDistance;
                        feature = f as Feature<Geometry>;
                    }
                });
            }
        }

        if (feature) {
            // Trouver la route parente de ce segment
            const parentRouteId = feature.get('parentRouteId');
            if (parentRouteId) {
                return this.routes.find(r => {
                    const routeId = r.equipmentId + '_' + r.dayKey + '_' + r.startTime.getTime();
                    return routeId === parentRouteId;
                }) || null;
            }
        }

        return null;
    }

    /**
     * Ajoute un listener de clic pour le mode split et merge
     */
    private addClickListener(): void {
        if (this.clickListener) {
            return; // Déjà ajouté
        }

        this.clickListener = (evt: MapBrowserEvent<any>) => {
            // Mode split
            if (this.splitMode) {
                evt.preventDefault();
                const clickCoordinate = evt.coordinate;

                // Trouver la route la plus proche du clic
                const result = this.findClosestRouteAndPosition(clickCoordinate);

                if (result) {
                    this.splitRouteAtPosition(result.route, result.positionIndex);
                    // Désactiver le mode split après la division
                    this.setSplitMode(false);
                    // Notifier le composant parent que le split est terminé
                    this.splitCompleted.next();
                }
                return;
            }

            // Mode merge
            if (this.mergeMode) {
                evt.preventDefault();
                const clickCoordinate = evt.coordinate;

                // Trouver la route la plus proche du clic
                const result = this.findClosestRouteAndPosition(clickCoordinate);

                if (result) {
                    if (!this.firstRouteToMerge) {
                        // Première route sélectionnée
                        this.firstRouteToMerge = result.route;
                        // Mettre en surbrillance la route sélectionnée
                        const highlightStyle = new Style({
                            stroke: new Stroke({
                                color: 'rgba(255, 255, 0, 1)', // Jaune vif
                                width: 6
                            })
                        });
                        result.route.feature.setStyle(highlightStyle);
                    } else {
                        // Deuxième route sélectionnée
                        if (result.route === this.firstRouteToMerge) {
                            // Même route, désélectionner
                            this.clearMergeSelection();
                        } else if (result.route.equipmentId !== this.firstRouteToMerge.equipmentId) {
                            // Routes de différents équipements, afficher un message
                            console.warn('Les routes doivent appartenir au même équipement');
                        } else {
                            // Fusionner les deux routes
                            this.mergeRoutes(this.firstRouteToMerge, result.route);
                            // Désactiver le mode merge après la fusion
                            this.setMergeMode(false);
                        }
                    }
                }
                return;
            }
        };

        this.map.on('singleclick', this.clickListener);
    }

    /**
     * Retire le listener de clic
     */
    private removeClickListener(): void {
        if (this.clickListener) {
            this.map.un('singleclick', this.clickListener);
            this.clickListener = null;
        }
    }

    /**
     * Ajoute un listener de hover pour le mode split et merge
     */
    private addHoverListener(): void {
        if (this.hoverListener) {
            return; // Déjà ajouté
        }

        this.hoverListener = (evt: MapBrowserEvent<any>) => {
            if (!this.splitMode && !this.mergeMode) {
                return;
            }

            // Réinitialiser le style de la feature précédemment survolée
            if (this.hoveredFeature) {
                // Supprimer les points de surbrillance
                const highlightPoints = this.hoveredFeature.get('highlightPoints');
                if (highlightPoints) {
                    highlightPoints.forEach((point: Feature<Geometry>) => {
                        this.source.removeFeature(point);
                    });
                    this.hoveredFeature.unset('highlightPoints');
                }
                this.hoveredFeature.changed();
                this.hoveredFeature = null;
            }

            // Trouver la feature sous le pointeur (seulement si dans le rayon de temps)
            // En mode split, une seule extrémité dans le rayon suffit
            const pixel = evt.pixel;
            const checkHover = this.splitMode
                ? (f: Feature<Geometry>) => this.isSegmentConnectedToTimeRadius(f)
                : (f: Feature<Geometry>) => this.isSegmentWithinTimeRadius(f);
            const feature = this.map.forEachFeatureAtPixel(pixel, (feature) => {
                if (feature.get('isVisualSegment') && checkHover(feature as Feature<Geometry>)) {
                    return feature as Feature<Geometry>;
                }
                return null;
            }, {
                layerFilter: (layer) => layer === this.layer
            });

            if (feature) {
                this.hoveredFeature = feature;
                // Appliquer un style avec contour surbrillant + pointillés
                const color = feature.get('color') || 'rgba(200, 200, 200, 0.8)';

                const geometry = feature.getGeometry() as LineString;
                const coordinates = geometry.getCoordinates();

                // Style de contour (surbrillance)
                const outlineStyle = new Style({
                    stroke: new Stroke({
                        color: 'rgba(255, 255, 0, 0.8)', // Jaune surbrillant
                        width: 6
                    })
                });

                // Style pointillé par-dessus
                const dashedStyle = new Style({
                    stroke: new Stroke({
                        color: color,
                        width: 3,
                        lineDash: [10, 5] // Pattern pointillé
                    })
                });

                // Style pour les points de jonction (début et fin du segment)
                const pointStyle = new Style({
                    image: new CircleStyle({
                        radius: 6,
                        fill: new Fill({
                            color: 'rgba(255, 255, 0, 0.9)' // Jaune surbrillant
                        }),
                        stroke: new Stroke({
                            color: 'rgba(255, 255, 255, 1)',
                            width: 2
                        })
                    })
                });

                // Créer des features pour les deux points de jonction
                const startPoint = new Feature({
                    geometry: new Point(coordinates[0])
                });
                startPoint.setStyle(pointStyle);

                const endPoint = new Feature({
                    geometry: new Point(coordinates[coordinates.length - 1])
                });
                endPoint.setStyle(pointStyle);

                // Stocker les points pour les supprimer plus tard
                feature.set('highlightPoints', [startPoint, endPoint]);

                // Ajouter les points à la source
                this.source.addFeature(startPoint);
                this.source.addFeature(endPoint);

                // Appliquer les deux styles (contour + pointillés) à la ligne
                feature.setStyle([outlineStyle, dashedStyle]);
            }
        };

        this.map.on('pointermove', this.hoverListener);
    }

    /**
     * Retire le listener de hover
     */
    private removeHoverListener(): void {
        if (this.hoverListener) {
            this.map.un('pointermove', this.hoverListener);
            this.hoverListener = null;
        }

        // Réinitialiser la feature survolée
        if (this.hoveredFeature) {
            // Supprimer les points de surbrillance
            const highlightPoints = this.hoveredFeature.get('highlightPoints');
            if (highlightPoints) {
                highlightPoints.forEach((point: Feature<Geometry>) => {
                    this.source.removeFeature(point);
                });
                this.hoveredFeature.unset('highlightPoints');
            }
            this.hoveredFeature.changed();
            this.hoveredFeature = null;
        }
    }

    /**
     * Vérifie si un segment de route (basé sur les positions) est dans le rayon de temps
     */
    private isPositionSegmentWithinTimeRadius(pos1Date: Date, pos2Date: Date): boolean {
        if (!this.currentFilterTime) {
            return true; // Pas de filtre de temps, tout est accessible
        }

        const filterTimeMs = this.currentFilterTime.getTime();
        const radiusStart = filterTimeMs - EquipmentRouteLayer.TIME_RADIUS_MS;
        const radiusEnd = filterTimeMs + EquipmentRouteLayer.TIME_RADIUS_MS;
        const segmentStartMs = new Date(pos1Date).getTime();
        const segmentEndMs = new Date(pos2Date).getTime();

        // Au moins un bout du segment doit être dans le rayon
        const startInRadius = segmentStartMs >= radiusStart && segmentStartMs <= radiusEnd;
        const endInRadius = segmentEndMs >= radiusStart && segmentEndMs <= radiusEnd;
        return startInRadius || endInRadius;
    }

    /**
     * Trouve la route la plus proche du point cliqué et l'index de position
     * Ne considère que les segments qui sont dans le rayon de temps de 10 minutes
     */
    private findClosestRouteAndPosition(clickCoordinate: Coordinate): {
        route: EquipmentRoute,
        positionIndex: number
    } | null {
        let closestRoute: EquipmentRoute | null = null;
        let closestPositionIndex = -1;
        let minDistance = Infinity;

        const pixel = this.map.getPixelFromCoordinate(clickCoordinate);
        const tolerancePixels = 15; // Tolérance en pixels

        this.routes.forEach(route => {
            const geometry = route.feature.getGeometry() as LineString;
            if (!geometry) return;

            const positions = route.feature.get('positions') as EquipmentPosition[];
            if (!positions || positions.length < 2) {
                return;
            }

            // Parcourir uniquement les segments dans le rayon de temps
            const coordinates = geometry.getCoordinates();
            for (let i = 0; i < coordinates.length - 1; i++) {
                // Vérifier si ce segment est dans le rayon de temps
                if (!this.isPositionSegmentWithinTimeRadius(positions[i].date, positions[i + 1].date)) {
                    continue; // Ignorer ce segment, il est hors du rayon de temps
                }

                const segmentStart = coordinates[i];
                const segmentEnd = coordinates[i + 1];

                // Calculer la distance du point de clic au segment (en coordonnées carte)
                const distanceToSegment = Math.sqrt(distToSegmentSquared(clickCoordinate, segmentStart, segmentEnd));

                // Convertir en pixels pour appliquer la tolérance
                const segmentLine = new LineString([segmentStart, segmentEnd]);
                const closestPointOnSegment = segmentLine.getClosestPoint(clickCoordinate);
                const closestPixel = this.map.getPixelFromCoordinate(closestPointOnSegment);
                const pixelDistance = Math.sqrt(
                    Math.pow(pixel[0] - closestPixel[0], 2) +
                    Math.pow(pixel[1] - closestPixel[1], 2)
                );

                // Vérifier que le clic est assez proche de CE segment spécifique
                if (pixelDistance <= tolerancePixels && distanceToSegment < minDistance) {
                    minDistance = distanceToSegment;
                    closestRoute = route;
                    // On divise *après* le premier point du segment le plus proche
                    closestPositionIndex = i;
                }
            }
        });

        if (closestRoute && closestPositionIndex !== -1) {
            // L'index de position pour le slice doit être le point de fin du segment
            const splitIndex = closestPositionIndex + 1;

            // S'assurer qu'on ne divise pas au tout début ou à la toute fin
            const totalPositions = (closestRoute.feature.get('positions') as EquipmentPosition[]).length;
            if (splitIndex > 0 && splitIndex < totalPositions) {
                return {
                    route: closestRoute,
                    positionIndex: splitIndex
                };
            }
        }

        return null;
    }

    /**
     * Divise une route en deux au niveau de la position spécifiée
     */
    private splitRouteAtPosition(route: EquipmentRoute, positionIndex: number): void {
        const positions = route.feature.get('positions') as EquipmentPosition[];

        if (!positions || positionIndex <= 0 || positionIndex >= positions.length) {
            return;
        }

        // Créer deux nouveaux ensembles de positions pour couper le lien
        const positions1 = positions.slice(0, positionIndex);
        const positions2 = positions.slice(positionIndex);

        // Supprimer l'ancienne route et ses segments visuels
        this.source.removeFeature(route.feature);

        // Supprimer aussi tous les segments visuels associés à cette route
        // Utiliser le startTime de la route pour identifier ses segments visuels
        const parentRouteId = route.equipmentId + '_' + route.dayKey + '_' + route.startTime.getTime();

        const featuresToRemove: Feature<Geometry>[] = [];
        this.source.getFeatures().forEach(feature => {
            if (feature.get('isVisualSegment')) {
                const featureParentId = feature.get('parentRouteId');
                if (featureParentId === parentRouteId) {
                    featuresToRemove.push(feature);
                }
            }
        });
        featuresToRemove.forEach(feature => this.source.removeFeature(feature));

        const routeIndex = this.routes.findIndex(r => r.feature === route.feature);
        if (routeIndex > -1) {
            this.routes.splice(routeIndex, 1);
            this.routesByEquipmentCache.clear();
        }

        // Supprimer l'ancienne route de la base si elle a un ID
        if (route.id) {
            this.routesDeleteRequired.next([route.id]);
        }

        // Créer les deux nouvelles routes seulement si elles ont assez de points
        const newRoutes: EquipmentRoute[] = [];
        if (positions1.length >= 2) {
            // La première route garde la couleur d'origine
            const route1 = this.createAndAddRoute(positions1, route.equipmentId, route.equipmentName, route.color);
            if (route1) newRoutes.push(route1);
        }
        if (positions2.length >= 2) {
            // La deuxième route prend une couleur différente (unique pour cet équipement)
            const newColor = this.getUniqueColorForEquipment(route.equipmentId, [route.color]);
            const route2 = this.createAndAddRoute(positions2, route.equipmentId, route.equipmentName, newColor);
            if (route2) newRoutes.push(route2);
        }

        // Émettre l'événement pour sauvegarder les routes
        if (newRoutes.length > 0) {
            const routeDtos = this.convertRoutesToDtos(newRoutes, this.siteId);
            this.routesSaveRequired.next(routeDtos);
        }

        // Forcer le rafraîchissement complet de la couche
        this.source.changed();
        this.layer.changed();
    }

    /**
     * Crée un segment de route à partir d'une liste de positions
     */
    private createAndAddRoute(
        positions: EquipmentPosition[],
        equipmentId: number,
        equipmentName: string,
        color: string,
        savedRouteData?: { id?: number, tirId?: number }
    ): EquipmentRoute | null {
        if (positions.length === 0) {
            return null;
        }

        const sortedPositions = positions.sort((a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        // Si une seule position, dupliquer le point pour créer un LineString valide
        const coordinates = sortedPositions.length === 1
            ? [fromLonLat([sortedPositions[0].longitude, sortedPositions[0].latitude]),
               fromLonLat([sortedPositions[0].longitude, sortedPositions[0].latitude])]
            : sortedPositions.map(pos => fromLonLat([pos.longitude, pos.latitude]));

        const startTime = new Date(sortedPositions[0].date);
        const endTime = new Date(sortedPositions[sortedPositions.length - 1].date);
        // Utiliser getShiftKey pour cohérence avec le groupement
        const dayKey = this.getShiftKey(startTime);

        const lineFeature = new Feature({
            geometry: new LineString(coordinates),
            equipmentId: equipmentId,
            positions: sortedPositions,
            dayKey: dayKey,
            startTime: startTime,
            endTime: endTime,
            isSelected: true, // Par défaut, la route est visible
            equipmentSelected: true, // Par défaut, l'équipement est sélectionné
            color: color // Attacher la couleur directement à la feature
        });

        const newRoute: EquipmentRoute = {
            id: savedRouteData?.id, // ID de la route en base (si sauvegardée)
            equipmentId: equipmentId,
            equipmentName: equipmentName,
            dayKey: dayKey,
            startTime: startTime,
            endTime: endTime,
            color: color,
            feature: lineFeature,
            isSelected: true, // Par défaut, la route est visible
            tirId: savedRouteData?.tirId // ID du tir assigné (si existant)
        };

        this.routes.push(newRoute);
        this.routesByEquipmentCache.clear();

        // Créer des segments visuels séparés entre chaque paire de positions
        // pour permettre l'opacité individuelle selon le temps
        for (let i = 0; i < sortedPositions.length - 1; i++) {
            const pos1 = sortedPositions[i];
            const pos2 = sortedPositions[i + 1];

            // Coordonnées du segment visuel
            const segmentCoords = [
                fromLonLat([pos1.longitude, pos1.latitude]),
                fromLonLat([pos2.longitude, pos2.latitude])
            ];

            // Dates du segment
            const segmentStartTime = new Date(pos1.date);
            const segmentEndTime = new Date(pos2.date);

            // Créer une feature visuelle pour ce segment
            const segmentFeature = new Feature({
                geometry: new LineString(segmentCoords),
                equipmentId: equipmentId,
                dayKey: dayKey,
                startTime: segmentStartTime,
                endTime: segmentEndTime,
                color: color,
                isSelected: true, // Par défaut visible
                equipmentSelected: true, // Par défaut, l'équipement est sélectionné
                isVisualSegment: true, // Marqueur pour identifier les segments visuels
                parentRouteId: equipmentId + '_' + dayKey + '_' + startTime.getTime() // ID unique basé sur le timestamp
            });

            this.source.addFeature(segmentFeature);
        }

        return newRoute;
    }

    /**
     * Convertit les routes en DTOs pour la sauvegarde
     */
    private convertRoutesToDtos(routes: EquipmentRoute[], siteId?: number): EquipmentRouteDto[] {
        return routes.map(route => ({
            equipmentId: route.equipmentId,
            siteId: siteId || 0, // Le siteId sera fourni par le composant
            start: route.startTime,
            end: route.endTime,
            tirId: undefined // Optionnel, peut être défini plus tard
        }));
    }

    /**
     * Retourne toutes les routes actuelles en DTOs
     */
    public getAllRoutesAsDto(siteId: number): EquipmentRouteDto[] {
        return this.convertRoutesToDtos(this.routes, siteId);
    }

    /**
     * Définit le siteId pour la sauvegarde des routes
     */
    public setSiteId(siteId: number): void {
        this.siteId = siteId;
    }

    /**
     * Charge les routes sauvegardées et les fusionne avec les routes générées automatiquement
     * Si un équipement a des routes sauvegardées, on ne dessine pas les routes automatiques
     */
    public loadSavedRoutes(savedRoutes: EquipmentRouteDto[], equipmentPositions: EquipmentPosition[], equipmentMap?: Map<number, any>): void {
        // Grouper les routes sauvegardées par équipement
        const savedRoutesByEquipment = new Map<number, EquipmentRouteDto[]>();
        savedRoutes.forEach(route => {
            if (!savedRoutesByEquipment.has(route.equipmentId)) {
                savedRoutesByEquipment.set(route.equipmentId, []);
            }
            savedRoutesByEquipment.get(route.equipmentId)!.push(route);
        });

        // Pour chaque équipement
        const equipmentIds = new Set(equipmentPositions.map(p => p.equipmentExternalId));
        equipmentIds.forEach(equipmentId => {
            const hasSavedRoutes = savedRoutesByEquipment.has(equipmentId);

            if (hasSavedRoutes) {
                // Charger les routes sauvegardées
                const routes = savedRoutesByEquipment.get(equipmentId)!;
                routes.forEach((routeDto) => {
                    // Filtrer les positions dans la plage de temps de cette route
                    const routePositions = equipmentPositions.filter(p =>
                        p.equipmentExternalId === equipmentId &&
                        new Date(p.date) >= new Date(routeDto.start) &&
                        new Date(p.date) <= new Date(routeDto.end)
                    );

                    if (routePositions.length >= 1) {
                        const equipmentName = equipmentMap?.get(equipmentId)?.displayName || `Equipment ${equipmentId}`;
                        // Générer une couleur unique pour cet équipement
                        const color = this.getUniqueColorForEquipment(equipmentId);
                        // Passer l'ID et le tirId de la route sauvegardée
                        this.createAndAddRoute(routePositions, equipmentId, equipmentName, color, {
                            id: routeDto.id,
                            tirId: routeDto.tirId
                        });
                    }
                });
            }
            // Si pas de routes sauvegardées, les routes automatiques seront créées normalement par createRoutesFromPositions
        });
    }

    /**
     * Active le mode édition pour un segment de route
     */
    public editSegment(segment: EquipmentRouteSegment, newPositions: EquipmentPosition[]) {
        // Trouver le segment à éditer
        const segmentToEdit = this.segments.find(s => s.feature === segment.feature);
        if (!segmentToEdit) {
            console.warn('Segment à éditer non trouvé:', segment);
            return;
        }

        // Mettre à jour les positions du segment

        // Mettre à jour la géométrie de la feature
        const coordinates = newPositions.map(pos => fromLonLat([pos.longitude, pos.latitude]));
        segmentToEdit.feature.setGeometry(new LineString(coordinates));

        // Mettre à jour les heures de début et fin
        const startTime = new Date(newPositions[0].date);
        const endTime = new Date(newPositions[newPositions.length - 1].date);
        segmentToEdit.feature.set("startTime", startTime);
        segmentToEdit.feature.set("endTime", endTime);

        // Forcer le rafraîchissement de la couche
        this.layer.changed();
    }

    /**
     * Libère tous les listeners enregistrés sur la carte.
     * À appeler quand la couche est détruite pour éviter les fuites mémoire.
     */
    public dispose(): void {
        this.map.un('dblclick', this.dblClickListener);
        this.removeClickListener();
        this.removeHoverListener();
    }
}

